import { clearElements } from "@services/storage/elements";
import { clearFilenames } from "@services/storage/filenames";
import { clearSkinini } from "@services/storage/skinini";
import { saveSkin } from "@services/api/project";
import { openDialog } from "@components/dialog";
import { byId, $, $$ } from "@utils/dom";
import { DATA_ATTR } from "@constants/data-attributes";

/**
 * Handle Save button click
 * Shows login dialog if user is a guest (dialog only exists for guests)
 * Saves skin to database and redirects to skin page
 */
async function handleSave(): Promise<void> {
  const notLoggedInDialog = byId("notLoggedInDialog");
  if (notLoggedInDialog) {
    await openDialog("notLoggedInDialog");
    return;
  }

  // Disable save buttons during operation
  const saveButtons = $$(DATA_ATTR.PROJECT_SAVE);
  saveButtons.forEach((button) => {
    button.setAttribute("disabled", "true");
    if (button instanceof HTMLButtonElement) {
      button.textContent = "Saving...";
    }
  });

  try {
    const result = await saveSkin();

    // Redirect to the new skin page
    window.location.href = result.redirect;
  } catch (error) {
    // Re-enable buttons
    saveButtons.forEach((button) => {
      button.removeAttribute("disabled");
      if (button instanceof HTMLButtonElement) {
        button.textContent = "Save";
      }
    });

    // Show error to user
    const message =
      error instanceof Error ? error.message : "Failed to save skin";
    alert(message);
  }
}

/**
 * Handle Reset button click
 * Clears all selected elements and filenames from localStorage
 * Optionally clears skinini if checkbox is checked
 */
async function handleReset(): Promise<void> {
  const dialog = byId("resetDialog");
  const resetSkininiCheckbox = dialog
    ? $<HTMLInputElement>('input[name="resetSkinini"]', dialog)
    : null;

  // Show confirmation dialog
  const result = await openDialog("resetDialog");

  if (result === "confirm") {
    // Clear both localStorage items
    clearElements();
    clearFilenames();

    // Clear skinini if checkbox was checked
    if (resetSkininiCheckbox?.checked) {
      clearSkinini();
    }

    // Reload the current page to reflect the reset
    window.location.reload();
  }
}

/**
 * Initialize project options handlers
 * Attaches click handlers to Reset and Save buttons
 */
export function initProjectOptions(): void {
  const resetButtons = $$(DATA_ATTR.PROJECT_RESET);
  const saveButtons = $$(DATA_ATTR.PROJECT_SAVE);

  resetButtons.forEach((button) => {
    button.addEventListener("click", handleReset);
  });

  saveButtons.forEach((button) => {
    button.addEventListener("click", handleSave);
  });
}
